import {inject} from "@angular/core";
import {
  HttpErrorResponse,
  type HttpEvent,
  type HttpHandlerFn,
  type HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import {BehaviorSubject, type Observable, throwError} from 'rxjs';
import {catchError, filter, first, switchMap} from "rxjs/operators";
import {AuthService} from "./auth.service";

const refreshSubject$ = new BehaviorSubject<null | boolean>(true);
const publicEndpoints = [
  '/api/auth/kiosk/users',
  '/api/auth/sign-in-url',
  '/api/auth/user',
  '/api/logs',
  '/api/sanctum/csrf-cookie',
  '/api/system-time',
];

function isPublicEndpoint(url: string): boolean {
  return publicEndpoints.some(entry => url.startsWith(entry));
}

function getCsrfToken(): string | null {
  // Extract XSRF-TOKEN from document.cookie (Sanctum standard)
  const name = 'XSRF-TOKEN=';
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(';');
  for (let c of ca) {
    c = c.trim();
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length) || null;
    }
  }
  return null;
}

function addCsrfHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
  const token = getCsrfToken();
  if (!!token && !['GET', 'HEAD', 'OPTIONS'].includes(request.method)) {
    return request.clone({
      setHeaders: {'X-XSRF-TOKEN': token},
    });
  }
  return request;
}

export function httpInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  console.log('httpInterceptor called for: ' + request.url);
  if (isPublicEndpoint(request.url)) {
    return next(request);
  }

  const auth = inject(AuthService);
  return next(request.clone({withCredentials: true})).pipe(
    catchError((error) => {
      // Check for error indicating refresh needed
      if (error instanceof HttpErrorResponse && (error.status === HttpStatusCode.Unauthorized || error.status === 419)) {
        const isRefreshing = refreshSubject$.value === null;
        console.log(`Auth error (${error.status}) for: ${request.url}; ${isRefreshing ? 'awaiting' : 'attempting'} refresh...`);
        // refresh user on 401 or csrf token on 419
        if (error.status === HttpStatusCode.Unauthorized) {
          // If a refresh is not already in progress, start one
          if (!isRefreshing) {
            handleUserRefresh(auth);
          }
        } else {
          if (!isRefreshing) {
            handleTokenRefresh(auth);
          }
        }

        // await the refresh
        return refreshSubject$.pipe(
          filter(result => result !== null), // Wait until it emits true or false
          first(),
          switchMap(success => {
            if (success) {
              // Retry on success with fresh CSRF (re-clone to pick up new cookie where applicable)
              const freshReq = addCsrfHeader(request.clone({withCredentials: true}));
              return next(freshReq);
            }
            // propagate error if refresh failed
            return throwError(() => error);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
}

function handleUserRefresh(auth: AuthService): void {
  refreshSubject$.next(null);

  auth.loadUser().then((success) => {
    console.log('load user success: ', success);
    refreshSubject$.next(success);
  });
}

function handleTokenRefresh(auth: AuthService): void {
  refreshSubject$.next(null);

  auth.refreshCsrfCookie().then((success) => {
    console.log('refresh token success: ', success);
    refreshSubject$.next(success);
  })
}
